import { useEffect, useRef, useState } from "react";

import { useI18n } from "../i18n/index.ts";
import { LEGEND_TREATS } from "../lib/spot-offer.ts";
import { LegalNotice } from "./legal-notice.tsx";
import { SpotIcon } from "./spot-icon.tsx";

/**
 * Légende et mentions légales, repliées derrière un bouton en bas à gauche.
 *
 * Elles occupaient le haut de l'écran, où elles disputaient la place à la
 * recherche et à l'action principale sans être consultées à chaque visite. Une
 * information de référence n'a pas besoin d'être affichée en permanence ; il
 * suffit qu'elle soit trouvable.
 */
export const MapInfo = (): React.ReactElement => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Un clic ailleurs referme le panneau : sur une carte, tout clic hors d'un
  // panneau flottant veut dire « je reviens à la carte ».
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onPointerDown = (event: PointerEvent): void => {
      if (
        !(event.target instanceof Node) ||
        containerRef.current?.contains(event.target) !== true
      ) {
        setOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    globalThis.addEventListener("pointerdown", onPointerDown);
    globalThis.addEventListener("keydown", onKeyDown);

    return () => {
      globalThis.removeEventListener("pointerdown", onPointerDown);
      globalThis.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="mapinfo" ref={containerRef}>
      {open && (
        <div className="mapinfo__panel">
          <h2 className="mapinfo__title">{t.legend.title}</h2>

          <ul className="legend" aria-label={t.legend.label}>
            {LEGEND_TREATS.map((treats) => (
              <li key={treats} className="legend__item">
                <SpotIcon treats={treats} size={24} />
                {t.treats[treats].label}
              </li>
            ))}
          </ul>

          <LegalNotice />
        </div>
      )}

      <button
        type="button"
        className="button button--round"
        aria-expanded={open}
        aria-label={t.legend.open}
        title={t.legend.open}
        onClick={() => setOpen((previous) => !previous)}
      >
        <span aria-hidden="true">i</span>
      </button>
    </div>
  );
};
